import { Vertex, Face } from "./geometry";
import { Subject } from "rxjs";

export type CollectionChangedEvent = {
    event: 'faces-added' | 'vertices-added' | 'faces-removed',
    vertices?: Vertex[],
    faces?: Face[]
};

export class Collection {

    public faces: Face[];
    public vertices: Vertex[];
    public observable: Subject<CollectionChangedEvent>;
    public forceRebuild: boolean;

    private compilationPattern: string;
    // TODO: Refactor to actual type
    private compilationPatternMap: any;
    private preAllocateBufferSize: number;
    private buffer: Float32Array;

    constructor() {
        this.faces = [];
        this.vertices = [];
        this.observable = new Subject<CollectionChangedEvent>();
        this.forceRebuild = true;
    }

    public calculateNormals(): Collection {
        this.faces.forEach(f => {
            f.calculateNormal();
            f.vertices.forEach(v => v.calculateNormal());
        });
        this.vertices.forEach(v => v.calculateNormal());

        return this;
    }
    public addFace(face: Face): Collection {
        this.faces.push(face);
        this.observable.next({ event: 'faces-added', faces: [face] });
        return this;
    }

    public addVertex(vertex: Vertex): Collection {
        // Limit max vertex length to uint16
        if (this.vertices.length === 0xFFFF) {
            throw Error(`Geometry vertex length exceeded ${0xFFFF} is the limit`);
        }

        this.vertices.push(vertex);
        this.observable.next({ event: 'vertices-added', vertices: [vertex] });
        return this;
    }

    public addFaces(faces: Face[]): Collection {
        faces.forEach(f => this.faces.push(f));

        this.observable.next({ event: 'faces-added', faces: faces });
        return this;
    }

    public addVertices(vertices: Vertex[]): Collection {
        vertices.forEach(v => this.vertices.push(v));

        this.observable.next({ event: 'vertices-added', vertices: vertices });
        return this;
    }

    public removeFace(face: Face): Collection {
        // Removing faces requires a forced recompile

        let faceIndex = this.faces.indexOf(face);
        if (faceIndex !== -1) {
            this.faces.splice(faceIndex, 1);

            for (let v = 0; v < face.vertices.length; v++) {
                let index = face.vertices[v].partOfFaces.indexOf(face);
                if (index !== -1) {
                    face.vertices[v].partOfFaces.splice(index, 1);
                }
            }
            this.observable.next({ event: 'faces-removed', faces: [face] });
        }
        return this;
    }

    public iterVertices(callback: (v: Vertex) => void): Collection {
        this.vertices.forEach(callback);
        return this;
    }

    public iterFaces(callback: (f: Face) => void): Collection {
        this.faces.forEach(callback);
        return this;
    }

    public setCompilationOptions(options: any): Collection {
        this.compilationPatternMap = options.customCompilationMethods || {};
        this.compilationPattern = options.compilationPattern || 'pnct'; // Default is position, normal, color, texture
        this.preAllocateBufferSize = options.bufferSize || 0; // the number of elements to hold

        this.compilationPatternMap['p'] = { 'length': 3, 'getValue': (face: Face, vertex: Vertex) => vertex.position };
        this.compilationPatternMap['n'] = { 'length': 3, 'getValue': (face: Face, vertex: Vertex) => vertex.normal };
        this.compilationPatternMap['c'] = { 'length': 4, 'getValue': (face: Face, vertex: Vertex) => vertex.color };
        this.compilationPatternMap['t'] = { 'length': 2, 'getValue': (face: Face, vertex: Vertex) => face.getTextureOffset(vertex) };
        this.compilationPatternMap['l'] = { 'length': 3, 'getValue': (face: Face, vertex: Vertex) => vertex.light };
        return this;
    }

    public initializeBuffer(elementCount: number, elementSize: number) {
        let requiredBufferSize = elementCount * elementSize; // TODO: Perhaps automatically assume expansion of say, 50%
        let preAllocation = this.preAllocateBufferSize * elementSize;

        if (this.buffer) {
            // Current buffer is not large enough, create new buffer
            if (this.buffer.length < requiredBufferSize) {
                this.forceRebuild = true;
                this.buffer = new Float32Array(preAllocation < requiredBufferSize ? requiredBufferSize : preAllocation);
            }
        } else {
            this.forceRebuild = true;
            this.buffer = new Float32Array(preAllocation < requiredBufferSize ? requiredBufferSize : preAllocation);
        }

        return this.buffer;
    }

    public compile(forceRecompile: boolean, filter?: any): CompileResult {

        let lengthPerVertex = 0;

        let dataLayout: DataLayoutType[] = [];

        for (let i = 0; i < this.compilationPattern.length; i++) {
            let e = this.compilationPattern[i];
            if (!this.compilationPatternMap[e]) {
                throw Error('unknown pattern symbol: ' + e);
            }

            dataLayout.push({
                identifier: e,
                offset: lengthPerVertex,
                length: this.compilationPatternMap[e].length
            });

            lengthPerVertex += this.compilationPatternMap[e].length;
        }

        let vertexCount = 0;

        // Faces are currently always triangles but for the sake of completeness calculate the total anyway
        for (let i = 0; i < this.faces.length; i++) {
            vertexCount += this.faces[i].vertices.length;
        }

        let dataBuffer = this.initializeBuffer(vertexCount, lengthPerVertex);

        let offset = 0;

        // Loop through all faces
        for (let face of this.faces) {

            if (!face.changed && !forceRecompile && !this.forceRebuild) {
                offset += face.vertices.length * lengthPerVertex;
                continue;
            }

            face.offset = offset;
            face.changed = false;

            // And the vertices within the face
            for (let vertex of face.vertices) {

                // Loop through the pattern and get the appropriate values for the face and vertex
                for (let i = 0; i < this.compilationPattern.length; i++) {

                    let data = this.compilationPatternMap[this.compilationPattern[i]].getValue(face, vertex);

                    if (!data) {
                        throw Error(`Data retrieved for pattern section ${this.compilationPattern[i]} contains no value`);
                    }

                    if (filter) {
                        data = filter(this.compilationPattern[i], data, face, vertex);
                    }

                    // Write the data to the buffer
                    for (let d = 0; d < data.length; d++) {
                        dataBuffer[offset++] = data[d];
                    }
                }
            }
        }
        this.forceRebuild = false;
        return {
            dataLayout: dataLayout,
            geometryData: dataBuffer,
            vertexLength: lengthPerVertex,
            vertexCount: vertexCount
        };
    }

    public recompileFace(face: Face) {
        face.changed = true;
        let offset = face.offset;

        // And the vertices within the face
        for (let vertex of face.vertices) {

            // Loop through the pattern and get the appropriate values for the face and vertex
            for (let i = 0; i < this.compilationPattern.length; i++) {

                let data = this.compilationPatternMap[this.compilationPattern[i]].getValue(face, vertex);

                if (!data) {
                    throw Error(`Data retrieved for pattern section ${this.compilationPattern[i]} contains no value`);
                }
                if (data.length !== this.compilationPatternMap[this.compilationPattern[i]].length) {
                    throw Error(`Data retrieved for pattern section ${this.compilationPattern[i]} is not of equal length`);
                }

                // Write the data to the buffer
                for (let d = 0; d < data.length; d++) {
                    this.buffer[offset++] = data[d];
                }
            }
        }

        return {
            offset: face.offset,
            length: offset - face.offset
        };
    }
}

export type DataLayoutType = {
    identifier: string | 'p' | 'n' | 'c' | 't',
    offset: number,
    length: number
}

export type CompileResult = {
    dataLayout: DataLayoutType[],
    geometryData: Float32Array,
    vertexLength: number,
    vertexCount: number
};